import type { IGeoFilter, Targetable } from '@feathr/blackbox';

import type {
  GeoFilterModel,
  IControlledProps,
  IProps,
  IUncontrolledProps,
} from './GeoFilter.hooks';

/**
 * Option for the geo filter type dropdown.
 */
export interface ITypeOption {
  id: string;
  name: string;
}

/**
 * Available geo filter type options for filtering search results.
 */
export const TYPE_OPTIONS: ITypeOption[] = [
  { id: 'all', name: 'All types' },
  { id: 'Country', name: 'Countries' },
  { id: 'Region', name: 'Regions' },
  { id: 'City', name: 'Cities' },
];

/**
 * Type guard to check if props are for controlled mode.
 *
 * @param props - The component props
 * @returns True if the component is in controlled mode
 */
export function isControlled(props: IProps): props is IControlledProps {
  return 'geoFilterId' in props;
}

/**
 * Converts a GeoFilterModel to a plain IGeoFilter object for use in the select.
 *
 * @param geofilter - The geo filter model (may be undefined or pending)
 * @returns The plain object representation, or undefined if not ready
 */
export function getGeoFilterValue(geofilter: GeoFilterModel | undefined): IGeoFilter | undefined {
  if (!geofilter) {
    return undefined;
  }
  if (geofilter.isPending) {
    return undefined;
  }
  return geofilter.toJS();
}

/**
 * Gets the current geo filter ID from props, handling both controlled and uncontrolled modes.
 *
 * @param props - The component props
 * @param geofilter - The current geo filter model (for uncontrolled mode)
 * @returns The current geo filter ID, or undefined
 */
export function getCurrentGeoFilterId(
  props: IProps,
  geofilter: GeoFilterModel | undefined,
): string | undefined {
  if (isControlled(props)) {
    return (props as IControlledProps).geoFilterId;
  }
  return geofilter?.id;
}

/**
 * Gets the set of already-used geo filter IDs to exclude from search results.
 *
 * @param props - The component props
 * @param getTargetable - Function to get a targetable by ID
 * @returns Set of used geo filter IDs
 */
export function getUsedGeoFilterIds(
  props: IProps,
  getTargetable: (id: string) => Targetable | undefined,
): Set<string> {
  if (isControlled(props)) {
    return (props as IControlledProps).usedGeoFilterIds ?? new Set<string>();
  }

  const uncontrolledProps = props as IUncontrolledProps;
  const usedIds = new Set<string>();

  uncontrolledProps.targetings.forEach((t) => {
    const targetableId = t.get('target_data');
    if (targetableId) {
      const targetable = getTargetable(targetableId);
      const geoFilterId = targetable?.get('geo_filter');
      if (geoFilterId) {
        usedIds.add(geoFilterId);
      }
    }
  });

  return usedIds;
}

/**
 * Gets the current included value from props, handling both controlled and uncontrolled modes.
 *
 * @param props - The component props
 * @returns Whether the filter is included (true) or excluded (false)
 */
export function getIncludedValue(props: IProps): boolean {
  if (isControlled(props)) {
    return (props as IControlledProps).included;
  }
  return (props as IUncontrolledProps).targeting.get('included');
}

/**
 * Generates a cache key for the async select based on the current state.
 *
 * @param props - The component props
 * @returns A unique cache key string
 */
export function getCacheKey(props: IProps): string {
  if (isControlled(props)) {
    return `geo-filter-controlled-${(props as IControlledProps).geoFilterId ?? 'new'}`;
  }
  return `geo-filter-${(props as IUncontrolledProps).targeting.id}`;
}
